import { Award, Check } from "lucide-react";

export const InstructorSection: React.FC<{ data: any; theme: any }> = ({
  data,
  theme,
}) => {
  if (!data?.name) return null;

  return (
    <section
      id="instructor"
      className="py-16 sm:py-20 bg-gradient-to-b from-gray-50 to-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900">
            {data.sectionTitle || "Meet Your Instructor"}
          </h2>
        </div>

        <div className="max-w-5xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200">
          <div className="grid md:grid-cols-5 gap-8">
            {/* Image */}
            <div className="md:col-span-2">
              {data.imageUrl ? (
                <img
                  src={data.imageUrl}
                  alt={data.name}
                  className="w-full h-full object-cover min-h-[400px]"
                />
              ) : (
                <div
                  className="w-full h-full min-h-[400px] flex items-center justify-center text-white text-6xl font-bold"
                  style={{ backgroundColor: theme?.primaryColor || "#10B981" }}
                >
                  {data.name.charAt(0)}
                </div>
              )}
            </div>

            {/* Info */}
            <div className="md:col-span-3 p-8">
              <h3 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
                {data.name}
              </h3>
              {data.title && (
                <p
                  className="text-xl font-medium mb-6"
                  style={{ color: theme?.primaryColor || "#10B981" }}
                >
                  {data.title}
                </p>
              )}

              {data.bio && (
                <p className="text-gray-700 leading-relaxed mb-6">{data.bio}</p>
              )}

              {/* Credentials */}
              {data.credentials && data.credentials.length > 0 && (
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Award
                      size={20}
                      style={{ color: theme?.primaryColor || "#10B981" }}
                    />
                    Credentials & Achievements
                  </h4>
                  <ul className="space-y-2">
                    {data.credentials.map((cred: string, i: number) => (
                      <li
                        key={i}
                        className="flex items-start gap-2 text-gray-700"
                      >
                        <Check
                          size={16}
                          className="mt-1 flex-shrink-0"
                          style={{ color: theme?.primaryColor || "#10B981" }}
                        />
                        <span>{cred}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Social Links */}
              {data.socialLinks &&
                Object.values(data.socialLinks).some((link) => link) && (
                  <div className="flex gap-3 pt-6 border-t border-gray-200">
                    {data.socialLinks.linkedin && (
                      <a
                        href={data.socialLinks.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-10 h-10 rounded-full flex items-center justify-center bg-gray-100 hover:bg-gray-200 transition-colors"
                        aria-label="LinkedIn"
                      >
                        <svg
                          className="w-5 h-5 text-gray-700"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                        </svg>
                      </a>
                    )}
                    {data.socialLinks.twitter && (
                      <a
                        href={data.socialLinks.twitter}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-10 h-10 rounded-full flex items-center justify-center bg-gray-100 hover:bg-gray-200 transition-colors"
                        aria-label="Twitter"
                      >
                        <svg
                          className="w-5 h-5 text-gray-700"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                        </svg>
                      </a>
                    )}
                    {data.socialLinks.website && (
                      <a
                        href={data.socialLinks.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-10 h-10 rounded-full flex items-center justify-center bg-gray-100 hover:bg-gray-200 transition-colors"
                        aria-label="Website"
                      >
                        <svg
                          className="w-5 h-5 text-gray-700"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
                          />
                        </svg>
                      </a>
                    )}
                    {data.socialLinks.youtube && (
                      <a
                        href={data.socialLinks.youtube}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-10 h-10 rounded-full flex items-center justify-center bg-gray-100 hover:bg-gray-200 transition-colors"
                        aria-label="YouTube"
                      >
                        <svg
                          className="w-5 h-5 text-gray-700"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                        </svg>
                      </a>
                    )}
                  </div>
                )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
